import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, MapPin, Phone, Mail, Send } from "lucide-react";
import { BUSINESS, waLink } from "../lib/api";
import api from "../lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = React.useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed! Watch your inbox for premium deals.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    }
  };

  return (
    <footer data-testid="site-footer" className="relative mt-32 border-t border-white/5 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-12 rounded-full bg-[#DC143C] flex items-center justify-center font-display text-2xl text-white shadow-lg shadow-[#DC143C]/30">
              F
            </span>
            <div>
              <div className="font-display text-2xl text-[#F5F5DC]">Faisal Car Bazar</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Premium Pre-Owned</div>
            </div>
          </div>
          <p className="text-[#A0A0A0] text-sm leading-relaxed mb-6">
            Nagpur's most trusted destination for pre-owned premium automobiles.
            Curated. Inspected. Delivered with confidence.
          </p>
          <div className="flex gap-3">
            <a
              href={BUSINESS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-[#F5F5DC] hover:bg-[#DC143C] hover:border-[#DC143C] transition-all"
            >
              <Instagram size={16} />
            </a>
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-[#F5F5DC] hover:bg-[#DC143C] hover:border-[#DC143C] transition-all"
            >
              <Facebook size={16} />
            </a>
            <a
              href={waLink("Hello Faisal Car Bazar!")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-[#F5F5DC] hover:bg-[#25D366] hover:border-[#25D366] transition-all"
            >
              <Send size={16} />
            </a>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="eyebrow mb-5">Explore</div>
          <ul className="space-y-3 text-sm">
            {["Home","Inventory","Services","Gallery","About","Contact"].map((l) => (
              <li key={l}>
                <Link
                  to={l === "Home" ? "/" : `/${l.toLowerCase()}`}
                  className="text-[#F5F5DC]/70 hover:text-[#D4AF37] transition-colors"
                >
                  {l}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow mb-5">Contact</div>
          <ul className="space-y-4 text-sm text-[#F5F5DC]/80">
            <li className="flex gap-3">
              <MapPin size={16} className="text-[#D4AF37] flex-shrink-0 mt-1" />
              <span className="leading-relaxed">{BUSINESS.address}</span>
            </li>
            <li className="flex gap-3 items-center">
              <Phone size={16} className="text-[#D4AF37]" />
              <a href={`tel:${BUSINESS.phone}`} className="hover:text-[#D4AF37]">
                {BUSINESS.phone}
              </a>
            </li>
            <li className="flex gap-3 items-center">
              <Mail size={16} className="text-[#D4AF37]" />
              <a href={`mailto:${BUSINESS.email}`} className="hover:text-[#D4AF37]">
                {BUSINESS.email}
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow mb-5">Newsletter</div>
          <p className="text-sm text-[#A0A0A0] mb-4 leading-relaxed">
            Get first access to newly arrived cars and exclusive offers.
          </p>
          <form onSubmit={subscribe} className="flex gap-2">
            <input
              data-testid="newsletter-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-[#1A1A1A] border border-white/10 rounded-full px-4 py-2.5 text-sm text-[#F5F5DC] placeholder:text-white/30 focus:border-[#DC143C] outline-none"
            />
            <button
              data-testid="newsletter-submit"
              type="submit"
              className="bg-[#DC143C] hover:bg-[#ED1C24] text-white px-4 py-2.5 rounded-full transition-colors"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/5 py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row justify-between gap-3 text-xs text-[#A0A0A0]">
          <span>© {new Date().getFullYear()} Faisal Car Bazar. All rights reserved.</span>
          <span>
            Crafted for Nagpur's automotive connoisseurs •{" "}
            <Link to="/admin/login" className="hover:text-[#D4AF37]">Admin</Link>
          </span>
        </div>
      </div>
    </footer>
  );
}
